import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

/**
 * Alert Dialog Component
 *
 * A modal dialog that interrupts the user with important content and expects a response.
 * Built on Radix UI Alert Dialog primitive following shadcn/ui design standards.
 *
 * @example
 * ```tsx
 * <AlertDialog>
 *   <AlertDialogTrigger asChild>
 *     <Button variant="destructive">Delete Account</Button>
 *   </AlertDialogTrigger>
 *   <AlertDialogContent>
 *     <AlertDialogHeader>
 *       <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
 *       <AlertDialogDescription>
 *         This action cannot be undone. This will permanently delete your account.
 *       </AlertDialogDescription>
 *     </AlertDialogHeader>
 *     <AlertDialogFooter>
 *       <AlertDialogCancel>Cancel</AlertDialogCancel>
 *       <AlertDialogAction>Continue</AlertDialogAction>
 *     </AlertDialogFooter>
 *   </AlertDialogContent>
 * </AlertDialog>
 * ```
 */
function AlertDialog({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
    return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}
AlertDialog.displayName = "AlertDialog";

/**
 * Alert Dialog Trigger Component
 *
 * The button that triggers the alert dialog.
 *
 * @example
 * ```tsx
 * <AlertDialogTrigger asChild>
 *   <Button>Open Dialog</Button>
 * </AlertDialogTrigger>
 * ```
 */
function AlertDialogTrigger({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
    return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}
AlertDialogTrigger.displayName = "AlertDialogTrigger";

/**
 * Alert Dialog Portal Component
 *
 * Portals the content to a different part of the DOM.
 *
 * @example
 * ```tsx
 * <AlertDialogPortal>
 *   <AlertDialogContent>...</AlertDialogContent>
 * </AlertDialogPortal>
 * ```
 */
function AlertDialogPortal({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
    return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}
AlertDialogPortal.displayName = "AlertDialogPortal";

/**
 * Alert Dialog Overlay Component
 *
 * A layer that covers the inert portion of the view when the dialog is open.
 *
 * @example
 * ```tsx
 * <AlertDialogOverlay />
 * ```
 */
const AlertDialogOverlay = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <AlertDialogPrimitive.Overlay
        ref={ref}
        data-slot="alert-dialog-overlay"
        className={cn(
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
            className
        )}
        {...props}
    />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

/**
 * Alert Dialog Content Component
 *
 * The main content container for the alert dialog.
 *
 * @example
 * ```tsx
 * <AlertDialogContent>
 *   <AlertDialogHeader>...</AlertDialogHeader>
 *   <AlertDialogFooter>...</AlertDialogFooter>
 * </AlertDialogContent>
 * ```
 */
const AlertDialogContent = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
    <AlertDialogPortal>
        <AlertDialogOverlay />
        <AlertDialogPrimitive.Content
            ref={ref}
            data-slot="alert-dialog-content"
            className={cn(
                "setu:bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 setu:fixed setu:top-[50%] left-[50%] setu:z-50 setu:grid setu:w-full setu:max-w-[calc(100%-2rem)] setu:translate-x-[-50%] setu:translate-y-[-50%] setu:gap-4 setu:rounded-lg setu:!border setu:p-6 setu:shadow-lg setu:duration-200 setu:sm:max-w-lg",
                className
            )}
            {...props}
        />
    </AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

/**
 * Alert Dialog Header Component
 *
 * Container for the dialog title and description.
 *
 * @example
 * ```tsx
 * <AlertDialogHeader>
 *   <AlertDialogTitle>Confirm Action</AlertDialogTitle>
 *   <AlertDialogDescription>Description here</AlertDialogDescription>
 * </AlertDialogHeader>
 * ```
 */
const AlertDialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-slot="alert-dialog-header"
            className={cn("setu:flex setu:flex-col setu:gap-2 setu:text-center setu:sm:text-left", className)}
            {...props}
        />
    )
);
AlertDialogHeader.displayName = "AlertDialogHeader";

/**
 * Alert Dialog Footer Component
 *
 * Container for action buttons.
 *
 * @example
 * ```tsx
 * <AlertDialogFooter>
 *   <AlertDialogCancel>Cancel</AlertDialogCancel>
 *   <AlertDialogAction>Continue</AlertDialogAction>
 * </AlertDialogFooter>
 * ```
 */
const AlertDialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-slot="alert-dialog-footer"
            className={cn("setu:flex setu:flex-col-reverse setu:gap-2 setu:sm:flex-row setu:sm:justify-end", className)}
            {...props}
        />
    )
);
AlertDialogFooter.displayName = "AlertDialogFooter";

/**
 * Alert Dialog Title Component
 *
 * An accessible title for the alert dialog.
 *
 * @example
 * ```tsx
 * <AlertDialogTitle>Are you sure?</AlertDialogTitle>
 * ```
 */
const AlertDialogTitle = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <AlertDialogPrimitive.Title
        ref={ref}
        data-slot="alert-dialog-title"
        className={cn("setu:text-lg setu:font-semibold", className)}
        {...props}
    />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

/**
 * Alert Dialog Description Component
 *
 * An accessible description for the alert dialog.
 *
 * @example
 * ```tsx
 * <AlertDialogDescription>
 *   This action cannot be undone.
 * </AlertDialogDescription>
 * ```
 */
const AlertDialogDescription = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <AlertDialogPrimitive.Description
        ref={ref}
        data-slot="alert-dialog-description"
        className={cn("setu:text-muted-foreground setu:text-sm", className)}
        {...props}
    />
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

/**
 * Alert Dialog Action Component
 *
 * The action button that confirms the alert dialog action.
 *
 * @example
 * ```tsx
 * <AlertDialogAction>Continue</AlertDialogAction>
 * ```
 */
const AlertDialogAction = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Action>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
    <AlertDialogPrimitive.Action
        ref={ref}
        data-slot="alert-dialog-action"
        className={cn(buttonVariants(), className)}
        {...props}
    />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

/**
 * Alert Dialog Cancel Component
 *
 * The button that cancels the alert dialog action.
 *
 * @example
 * ```tsx
 * <AlertDialogCancel>Cancel</AlertDialogCancel>
 * ```
 */
const AlertDialogCancel = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
    <AlertDialogPrimitive.Cancel
        ref={ref}
        data-slot="alert-dialog-cancel"
        className={cn(buttonVariants({ variant: "outline" }), className)}
        {...props}
    />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
    AlertDialog,
    AlertDialogPortal,
    AlertDialogOverlay,
    AlertDialogTrigger,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogAction,
    AlertDialogCancel,
};
